import type { z } from 'zod';
import type {
  realtimeInputSchema,
  realtimeProtocolSchema,
  realtimeSidebandSchema,
  realtimeVoiceSchema,
} from './realtime';
import {
  joinPipelineCall,
  type RealtimePipelineAnswer,
  type RealtimePipelineModels,
} from './realtime-pipeline';
import { joinCallRoom } from './realtime-room';

/**
 * A `realtime.v1` call over WebRTC, as OpenAI's GPT-Live sessions run it. The peer connection,
 * audio and events stay with the caller (the page or an app's view); only signaling goes to the
 * user's provider (`call.create`), through whoever may call it for the caller. The call's events
 * travel on its `oai-events` data channel: its transcripts as timed fragments, the tasks it
 * delegates to the client (without their words: the client rebuilds them from the transcript),
 * and what the client appends for it to say or know. The core may answer its own room instead
 * (`realtime-room.ts`: its pipeline, a group's voices), whose events are told the same.
 */
export type RealtimeEvent = Record<string, unknown> & { type?: string };
/**
 * How a call runs: a provider's over WebRTC (`realtime.v1`), the core's pipeline in the page
 * (dictation), or the core's room, where the server speaks itself.
 */
export type RealtimeTransport = 'webrtc' | 'pipeline' | 'room';
/** The events a WebRTC call speaks (`realtime.v1` `call.create`'s `protocol`). */
export type RealtimeProtocol = z.infer<typeof realtimeProtocolSchema>;
export type RealtimeSideband = z.infer<typeof realtimeSidebandSchema>;
export type RealtimeCall = {
  sessionId: string | null;
  transport: RealtimeTransport;
  /** How its client events are written (the core's pipeline and room take `v1`'s). */
  protocol: RealtimeProtocol;
  /** Sends a client event to the call; false when it is closed (or cannot take it). */
  send(event: RealtimeEvent): boolean;
  /**
   * Ends the call: asks the session to close (`session.close`), keeps the connection until it
   * says it did (`session.closed`) or a few seconds passed, then closes it: what the session
   * sends meanwhile (a task it had just delegated) is still told. Settles once closed. Idempotent.
   */
  close(): Promise<void>;
};

/** A silent microphone stand-in, for calls that only listen or speak. Closing is idempotent. */
export function createSilentAudioStream() {
  if (typeof AudioContext === 'undefined') throw new Error('Web Audio is unavailable.');
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const destination = context.createMediaStreamDestination();
  gain.gain.value = 0;
  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start();
  void context.resume().catch(() => undefined);
  let closed = false;
  return {
    stream: destination.stream,
    close() {
      if (closed) return;
      closed = true;
      oscillator.stop();
      destination.stream.getTracks().forEach((track) => {
        track.stop();
      });
      void context.close();
    },
  };
}

/** How long the call may take to connect, then to start its session. */
const CONNECT_TIMEOUT_MS = 15_000;
/** How long a closing call waits for its session to say it closed. */
const CLOSE_TIMEOUT_MS = 3_000;

function waitFor(
  target: EventTarget,
  done: (event: Event) => boolean,
  failure: string,
  signal?: AbortSignal,
  ready = false,
) {
  if (ready) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => finish(new Error(failure)), CONNECT_TIMEOUT_MS);
    const finish = (error?: Error) => {
      clearTimeout(timeout);
      target.removeEventListener('open', heard);
      target.removeEventListener('message', heard);
      target.removeEventListener('error', failed);
      signal?.removeEventListener('abort', aborted);
      error ? reject(error) : resolve();
    };
    const heard = (event: Event) => {
      if (done(event)) finish();
    };
    const failed = () => finish(new Error('The voice call failed.'));
    const aborted = () => finish(new DOMException('Cancelled', 'AbortError'));
    target.addEventListener('open', heard);
    target.addEventListener('message', heard);
    target.addEventListener('error', failed, { once: true });
    signal?.addEventListener('abort', aborted, { once: true });
  });
}

function parseEvent(data: unknown): RealtimeEvent | null {
  if (typeof data !== 'string') return null;
  try {
    const event: unknown = JSON.parse(data);
    return event && typeof event === 'object' && !Array.isArray(event)
      ? (event as RealtimeEvent)
      : null;
  } catch {
    return null;
  }
}

export type RealtimeVoice = z.infer<typeof realtimeVoiceSchema>;
export type RealtimeInput = z.infer<typeof realtimeInputSchema>;
/** What the call's session is created with (`realtime.v1` `call.create`). */
export type RealtimeSession = {
  instructions?: string;
  input?: RealtimeInput[];
  voice?: RealtimeVoice;
  /** What the user speaks (ISO 639-1), when known. */
  language?: string;
};
/**
 * What a call's signaling answers: a provider's SDP answer (`call.create`), the core's pipeline
 * of the user's models in the page, or the core's room to join (Settings › Voice).
 */
export type RealtimeAnswer =
  | {
      transport: 'webrtc';
      sdp: string;
      sessionId: string | null;
      protocol: RealtimeProtocol;
      sideband?: RealtimeSideband;
    }
  | {
      transport: 'room';
      /** The room's server (`wss://…`) and the caller's token to join it. */
      url: string;
      token: string;
    }
  | RealtimePipelineAnswer;
/** The data topic of a core room, which tells the call's events. */
export const CALL_ROOM_TOPIC = 'crowdex.call';
/** The call's signaling: an SDP offer in, the answer out. */
export type RealtimeSignaling = (sdp: string, session: RealtimeSession) => Promise<RealtimeAnswer>;

/** A client event id: the session's acknowledgement names it back (`client_event_id`). */
export const realtimeEventId = () => `crowdex_${crypto.randomUUID().replaceAll('-', '')}`;

export async function createRealtimeCall(options: {
  signaling: RealtimeSignaling;
  stream?: MediaStream;
  instructions?: string;
  /** Messages the session starts with, as context. */
  input?: RealtimeInput[];
  voice?: RealtimeVoice;
  /** What the user speaks (ISO 639-1), when known. */
  language?: string;
  signal?: AbortSignal;
  onEvent?: (event: RealtimeEvent) => void;
  /** A voice of the call: the session's, or one of a room's (`voice`, its participant). */
  onRemoteStream?: (stream: MediaStream, voice?: string) => void;
  /** The user's models, for a call on the core's pipeline that answers and speaks. */
  models?: RealtimePipelineModels;
}): Promise<RealtimeCall> {
  if (typeof RTCPeerConnection === 'undefined') throw new Error('WebRTC is unavailable.');
  const peerConnection = new RTCPeerConnection();
  const dataChannel = peerConnection.createDataChannel('oai-events');
  const fallback = options.stream ? null : createSilentAudioStream();
  let started = false;
  let ended = false;
  let closed = () => {};
  const done = new Promise<void>((resolve) => {
    closed = resolve;
  });
  const teardown = () => {
    if (ended) return;
    ended = true;
    closed();
    try {
      dataChannel.close();
    } catch {}
    fallback?.close();
    peerConnection.close();
  };
  const send = (event: RealtimeEvent) => {
    if (ended || dataChannel.readyState !== 'open') return false;
    dataChannel.send(JSON.stringify(event));
    return true;
  };
  let closing = false;
  const close = () => {
    if (closing || ended) return done;
    closing = true;
    if (!started || !send({ type: 'session.close', event_id: realtimeEventId() })) {
      teardown();
      return done;
    }
    const timer = setTimeout(teardown, CLOSE_TIMEOUT_MS);
    dataChannel.addEventListener('message', ({ data }) => {
      if (parseEvent(data)?.type !== 'session.closed') return;
      clearTimeout(timer);
      teardown();
    });
    return done;
  };
  const aborted = () => teardown();
  options.signal?.addEventListener('abort', aborted, { once: true });
  try {
    // Listeners first: the session may speak as soon as the channel opens.
    dataChannel.addEventListener('message', ({ data }) => {
      const event = parseEvent(data);
      if (event === null) return;
      if (event.type === 'session.started') started = true;
      options.onEvent?.(event);
    });
    if (options.onRemoteStream)
      peerConnection.addEventListener('track', (event) =>
        options.onRemoteStream?.(event.streams[0] ?? new MediaStream([event.track])),
      );
    const stream = options.stream ?? fallback?.stream;
    if (!stream) throw new Error('The voice call could not create an audio input stream.');
    for (const track of stream.getAudioTracks()) peerConnection.addTrack(track, stream);
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);
    if (!offer.sdp) throw new Error('The browser did not create a WebRTC offer.');
    const answer = await options.signaling(offer.sdp, {
      ...(options.instructions ? { instructions: options.instructions } : {}),
      ...(options.input?.length ? { input: options.input } : {}),
      ...(options.voice ? { voice: options.voice } : {}),
      ...(options.language ? { language: options.language } : {}),
    });
    if (options.signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (answer.transport === 'pipeline') {
      ended = true;
      dataChannel.close();
      peerConnection.close();
      return await joinPipelineCall({
        answer,
        stream,
        release: () => fallback?.close(),
        ...(options.models === undefined ? {} : { models: options.models }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
        ...(options.onEvent === undefined ? {} : { onEvent: options.onEvent }),
        ...(options.onRemoteStream === undefined ? {} : { onRemoteStream: options.onRemoteStream }),
      });
    }
    if (answer.transport === 'room') {
      ended = true;
      dataChannel.close();
      peerConnection.close();
      return await joinCallRoom({
        url: answer.url,
        token: answer.token,
        topic: CALL_ROOM_TOPIC,
        stream,
        release: () => fallback?.close(),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
        ...(options.onEvent === undefined ? {} : { onEvent: options.onEvent }),
        ...(options.onRemoteStream === undefined ? {} : { onRemoteStream: options.onRemoteStream }),
      });
    }
    // The session starts once the channel is open; nothing is sent before it says so.
    const sessionStarted = waitFor(
      dataChannel,
      (event) => event.type === 'message' && started,
      'The voice call did not start.',
      options.signal,
      started,
    );
    await peerConnection.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
    await waitFor(
      dataChannel,
      (event) => event.type === 'open',
      'The voice call did not connect.',
      options.signal,
      dataChannel.readyState === 'open',
    );
    await sessionStarted;
    return {
      sessionId: answer.sessionId,
      transport: 'webrtc',
      protocol: answer.protocol,
      send,
      close,
    };
  } catch (error) {
    if (ended) fallback?.close();
    teardown();
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', aborted);
  }
}

/** What was said in the call, as its transcripts stream it: times are from the session's start. */
export type RealtimeFragment = {
  role: 'user' | 'assistant';
  text: string;
  startMs: number;
  endMs: number;
};

const milliseconds = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Words of the call's transcripts, when the event is one: `session.input_transcript.delta` for
 * the user and `session.output_transcript.delta` for the voice (`v1`), or
 * `input_transcript.added` / `output_transcript.added` (`v2`). They come in pieces: who said
 * what when is all a call uses (`v2`'s turns repeat them).
 */
export function realtimeFragment(event: RealtimeEvent): RealtimeFragment | null {
  const role =
    event.type === 'session.input_transcript.delta' || event.type === 'input_transcript.added'
      ? 'user'
      : event.type === 'session.output_transcript.delta' || event.type === 'output_transcript.added'
        ? 'assistant'
        : null;
  const text = event.type?.startsWith('session.')
    ? event['delta']
    : record(event['item'])?.['text'];
  if (role === null || typeof text !== 'string' || text === '') return null;
  const startMs = milliseconds(event['start_ms']);
  const endMs = milliseconds(event['end_ms']);
  return {
    role,
    text,
    startMs: startMs ?? endMs ?? 0,
    endMs: Math.max(endMs ?? startMs ?? 0, startMs ?? 0),
  };
}

/** A task the call delegates to the client. */
export type RealtimeDelegation = {
  id: string;
  /** When in the session it was decided. */
  offsetMs: number;
  /** What it asks, when the call says it (`v2`); `v1` delegations carry none of the words. */
  text: string | null;
};

/**
 * A task the call delegates to the client, when the event is one: `session.delegation.created`
 * (`v1`), or `delegation.created` (`v2`), whose item carries the request's text.
 */
export function realtimeDelegation(event: RealtimeEvent): RealtimeDelegation | null {
  const v2 = event.type === 'delegation.created';
  if (!v2 && event.type !== 'session.delegation.created') return null;
  const values = record(v2 ? event['item'] : event['delegation']);
  const id = values?.['id'];
  if (
    values === null ||
    values['target'] !== 'client' ||
    typeof id !== 'string' ||
    !/^[A-Za-z0-9_-]{1,200}$/.test(id)
  )
    return null;
  const content = Array.isArray(values['content']) ? values['content'] : [];
  const text = content
    .flatMap((part) => {
      const words = record(part)?.['text'];
      return typeof words === 'string' ? [words] : [];
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return {
    id,
    offsetMs: milliseconds(event['offset_ms']) ?? Number.POSITIVE_INFINITY,
    text: v2 && text ? text : null,
  };
}

/** A speaker's line: their fragments with no long pause between them. */
export type RealtimeLine = RealtimeFragment;

/** A pause this long between a speaker's fragments starts a new line. */
export const LINE_PAUSE_MS = 1_500;

/** Groups fragments, in the order said, into lines: consecutive words of a speaker. */
export function realtimeLines(
  fragments: readonly RealtimeFragment[],
  pauseMs = LINE_PAUSE_MS,
): RealtimeLine[] {
  const lines: RealtimeLine[] = [];
  for (const fragment of [...fragments].sort((a, b) => a.startMs - b.startMs)) {
    const last = lines.at(-1);
    if (
      last !== undefined &&
      last.role === fragment.role &&
      fragment.startMs - last.endMs < pauseMs
    )
      lines[lines.length - 1] = {
        ...last,
        text: last.text + fragment.text,
        endMs: Math.max(last.endMs, fragment.endMs),
      };
    else lines.push({ ...fragment });
  }
  return lines.flatMap((line) => {
    const text = line.text.replace(/\s+/g, ' ').trim();
    return text ? [{ ...line, text }] : [];
  });
}

/** How long a delegation waits for the user's last words that asked for it, when they come late. */
export const DELEGATION_GRACE_MS = 300;

/**
 * The call's transcript, kept to rebuild what each delegation asks: the user's words that ended
 * by the time it was decided, since the previous one. Fragments only go: what was already taken
 * for a task is not taken again.
 */
export class RealtimeTranscript {
  readonly fragments: RealtimeFragment[] = [];
  /** The fragments taken as tasks. */
  readonly tasks = new Set<RealtimeFragment>();
  #delegatedUntil = Number.NEGATIVE_INFINITY;

  /** Keeps a fragment; its event's, as `realtimeFragment` reads it. */
  add(fragment: RealtimeFragment): void {
    this.fragments.push(fragment);
  }

  /** When the voice last stopped speaking before `offsetMs`, since the previous delegation. */
  #answeredBy(offsetMs: number): number {
    return this.fragments.reduce(
      (end, fragment) =>
        fragment.role === 'assistant' &&
        fragment.endMs > this.#delegatedUntil &&
        fragment.endMs <= offsetMs
          ? Math.max(end, fragment.endMs)
          : end,
      Number.NEGATIVE_INFINITY,
    );
  }

  /**
   * What the delegation decided at `offsetMs` asks: the user's words since the voice last spoke
   * before it, and since the previous delegation (null when none: the call delegated without
   * words to go on).
   */
  delegation(offsetMs: number): { task: string; fragments: RealtimeFragment[] } | null {
    const since = (fragment: RealtimeFragment) =>
      fragment.role === 'user' &&
      !this.tasks.has(fragment) &&
      fragment.endMs > this.#delegatedUntil;
    const open = this.fragments.filter(since);
    const answered = this.#answeredBy(offsetMs);
    const unanswered = open.filter((fragment) => fragment.startMs >= answered);
    // Words started before the decision (their last ones may be timed a little after it); else
    // words started after it, the transcript being late.
    let fragments = unanswered.filter((fragment) => fragment.startMs < offsetMs);
    if (fragments.length === 0) fragments = unanswered;
    // The voice may have said a few words before deciding: the words before them, then.
    if (fragments.length === 0) fragments = open.filter((fragment) => fragment.startMs < offsetMs);
    if (fragments.length === 0) fragments = open;
    // The request is what the user said last, since the voice last spoke to them.
    const last = fragments.reduce<RealtimeFragment | null>(
      (latest, fragment) =>
        latest === null || fragment.startMs > latest.startMs ? fragment : latest,
      null,
    );
    if (last !== null) {
      const answered = this.fragments.reduce(
        (end, fragment) =>
          fragment.role === 'assistant' &&
          fragment.endMs > this.#delegatedUntil &&
          fragment.endMs <= last.startMs
            ? Math.max(end, fragment.endMs)
            : end,
        Number.NEGATIVE_INFINITY,
      );
      fragments = fragments.filter((fragment) => fragment.startMs >= answered);
    }
    this.#delegatedUntil = Math.max(
      this.#delegatedUntil,
      Number.isFinite(offsetMs) ? offsetMs : this.endMs,
      ...fragments.map((fragment) => fragment.endMs),
    );
    for (const fragment of fragments) this.tasks.add(fragment);
    const task = realtimeLines(fragments, Number.POSITIVE_INFINITY)
      .map((line) => line.text)
      .join(' ')
      .trim();
    return task ? { task, fragments } : null;
  }

  /**
   * Whether the user's words a delegation decided at `offsetMs` asks are in: words since the
   * voice last spoke before it. The call may decide before their transcript comes; until then,
   * the words before are what the voice answered already, not the request.
   */
  heardRequest(offsetMs: number): boolean {
    const answered = this.#answeredBy(offsetMs);
    return this.fragments.some(
      (fragment) =>
        fragment.role === 'user' &&
        !this.tasks.has(fragment) &&
        fragment.endMs > this.#delegatedUntil &&
        fragment.startMs >= answered,
    );
  }

  /**
   * The user's latest words, since the voice last spoke before them, unless a delegation took
   * them: what they just asked, which the client may answer itself (then it `take`s them).
   */
  latestRequest(): { text: string; fragments: RealtimeFragment[] } | null {
    const last = this.fragments.reduce<RealtimeFragment | null>(
      (latest, fragment) =>
        fragment.role === 'user' && (latest === null || fragment.startMs > latest.startMs)
          ? fragment
          : latest,
      null,
    );
    if (last === null || this.tasks.has(last)) return null;
    const open = this.fragments.filter(
      (fragment) =>
        fragment.role === 'user' &&
        !this.tasks.has(fragment) &&
        fragment.endMs > this.#delegatedUntil,
    );
    const answered = this.fragments.reduce(
      (end, fragment) =>
        fragment.role === 'assistant' && fragment.endMs <= last.startMs
          ? Math.max(end, fragment.endMs)
          : end,
      Number.NEGATIVE_INFINITY,
    );
    const fragments = open.filter((fragment) => fragment.startMs >= answered);
    const text = realtimeLines(fragments, Number.POSITIVE_INFINITY)
      .map((line) => line.text)
      .join(' ')
      .trim();
    return text ? { text, fragments } : null;
  }

  /** Takes these words as answered by the client: no delegation takes them as its task. */
  take(fragments: readonly RealtimeFragment[]): void {
    for (const fragment of fragments) this.tasks.add(fragment);
  }

  /** The lines said in `(sinceMs, untilMs]`, both speakers, without the words taken as tasks. */
  lines(
    options: {
      sinceMs?: number;
      untilMs?: number;
      skip?: (fragment: RealtimeFragment) => boolean;
    } = {},
  ): RealtimeLine[] {
    const since = options.sinceMs ?? Number.NEGATIVE_INFINITY;
    const until = options.untilMs ?? Number.POSITIVE_INFINITY;
    return realtimeLines(
      this.fragments.filter(
        (fragment) =>
          fragment.endMs > since &&
          fragment.endMs <= until &&
          !this.tasks.has(fragment) &&
          !options.skip?.(fragment),
      ),
    );
  }

  /** When the last word heard ended. */
  get endMs(): number {
    return this.fragments.reduce((end, fragment) => Math.max(end, fragment.endMs), 0);
  }
}

/** The most an append carries, in UTF-8 bytes: within the API's 500 tokens, whatever the text. */
const APPEND_MAX_BYTES = 500;

/** Cuts `text` into parts of at most `APPEND_MAX_BYTES`, between words when it can. */
export function speechParts(text: string): string[] {
  const encoder = new TextEncoder();
  const parts: string[] = [];
  let rest = text;
  while (encoder.encode(rest).length > APPEND_MAX_BYTES) {
    // The longest prefix that fits, by code points.
    let end = 0;
    let bytes = 0;
    for (const char of rest) {
      const size = encoder.encode(char).length;
      if (bytes + size > APPEND_MAX_BYTES) break;
      bytes += size;
      end += char.length;
    }
    const space = rest.lastIndexOf(' ', end);
    const cut = space > end / 2 ? space + 1 : end;
    parts.push(rest.slice(0, cut));
    rest = rest.slice(cut);
  }
  if (rest) parts.push(rest);
  return parts;
}

/**
 * The client events that append `text` to a call speaking `protocol`, in parts within the API's
 * limit: words for it to say (`commentary`, paraphrased), facts for its next replies (`thinking`,
 * not said), or instructions (`instructions`, which may cut what it says; `v2` takes them as
 * facts). An answer names its delegation; anything else goes with none.
 */
export function realtimeAppends(
  protocol: RealtimeProtocol,
  kind: 'commentary' | 'thinking' | 'instructions',
  text: string,
  delegationId: string | null = null,
): RealtimeEvent[] {
  if (!text.trim()) return [];
  return speechParts(text).map((part) =>
    protocol === 'v1'
      ? {
          type: `session.${kind}.append`,
          event_id: realtimeEventId(),
          delegation_id: delegationId,
          content: part,
        }
      : {
          type: delegationId === null ? 'session.context.append' : 'delegation.context.append',
          ...(delegationId === null ? {} : { delegation_item_id: delegationId }),
          channel: kind === 'commentary' ? 'speakable' : 'commentary',
          content: [{ type: 'input_text', text: part }],
        },
  );
}
